import { AuthErrorCode } from '@cretesuite/api-client-auth'

/**
 * What the app does with an auth error from api-client-auth:
 * - silent: nothing to show (offline, or no session to begin with)
 * - logout: the session is gone, tell the user and force a login
 * - switchRejected: the server refused an organization switch, keep the session
 * - generic: anything else
 */
export type AuthErrorOutcome =
  | 'silent'
  | 'logout'
  | 'switchRejected'
  | 'generic'

export const classifyAuthError = (
  error: { code?: AuthErrorCode },
  isOnline: boolean,
): AuthErrorOutcome => {
  // A failed token refresh due to no connection must not log the user out
  if (!isOnline) return 'silent'
  const { code } = error
  if (code === AuthErrorCode.refreshTokenNotFound) return 'silent'
  // notAuthorized comes from the token refresh, which clears the session first
  const isSessionGone =
    code === AuthErrorCode.refreshTokenExpired ||
    code === AuthErrorCode.refreshTokenInvalid ||
    code === AuthErrorCode.notAuthorized
  if (isSessionGone) return 'logout'
  if (code === AuthErrorCode.switchRejected) return 'switchRejected'
  return 'generic'
}

/** Why the server refused an organization switch. */
export type SwitchRejectionReason =
  | 'freshLogin'
  | 'operatorMismatch'
  | 'unknown'

// Server `extensions.reason` codes (SwitchOrganizationService.ts)
const REASON_BY_SERVER_CODE: Record<string, SwitchRejectionReason> = {
  FRESH_LOGIN_REQUIRED: 'freshLogin',
  // The org was added after this login: a new login refreshes the allow-list
  TARGET_NOT_ALLOWED: 'freshLogin',
  TARGET_PERSON_MISMATCH: 'operatorMismatch',
}

export const switchRejectionReason = (error: {
  reason?: string
  message?: string
}): SwitchRejectionReason => {
  if (error.reason) return REASON_BY_SERVER_CODE[error.reason] ?? 'unknown'
  // APIs deployed before `extensions.reason` only send the message
  const text = error.message ?? ''
  if (/fresh login/i.test(text)) return 'freshLogin'
  if (/not found in target organization/i.test(text)) return 'operatorMismatch'
  return 'unknown'
}
